import { useEffect, useRef } from 'react';
import { Ruler, Trash2 } from 'lucide-react';
import { Vector3 } from 'three';
import { formatDistance } from '@/viewer/measure';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';

// web-ifc normalises all geometry to metres, so scene-space distances are
// always in metres regardless of the IFC file's declared length unit.
const SCENE_UNIT = 'm';

export function MeasureOverlay() {
  const measureMode = useViewerStore((s) => s.measureMode);
  const measurements = useViewerStore((s) => s.measurements);
  const { getViewer, clearMeasurements } = useViewer();
  const labelsRef = useRef<HTMLDivElement>(null);

  // Labels track their 3D midpoints every frame; positions are written
  // straight to the DOM to avoid re-rendering React at 60fps.
  useEffect(() => {
    if (measurements.length === 0) return;
    let rafId = 0;
    const v = new Vector3();
    const tick = (): void => {
      const viewer = getViewer();
      const container = labelsRef.current;
      if (viewer && container) {
        const canvas = viewer.renderer.domElement;
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        for (const child of Array.from(container.children) as HTMLElement[]) {
          const mid = (child.dataset['mid'] ?? '').split(',').map(Number);
          v.set(mid[0] ?? 0, mid[1] ?? 0, mid[2] ?? 0).project(viewer.camera);
          const behind = v.z > 1;
          child.style.display = behind ? 'none' : '';
          if (!behind) {
            child.style.left = `${((v.x + 1) / 2) * w}px`;
            child.style.top = `${((1 - v.y) / 2) * h}px`;
          }
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [getViewer, measurements]);

  return (
    <>
      {measurements.length > 0 && (
        <div ref={labelsRef} className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
          {measurements.map((m) => (
            <div
              key={m.id}
              data-mid={[
                (m.a[0] + m.b[0]) / 2,
                (m.a[1] + m.b[1]) / 2,
                (m.a[2] + m.b[2]) / 2,
              ].join(',')}
              className="absolute -translate-x-1/2 -translate-y-[130%] rounded-md border border-amber-400/40 bg-black/75 px-1.5 py-0.5 text-[11px] whitespace-nowrap text-amber-300 backdrop-blur-sm"
            >
              {formatDistance(m.distance, SCENE_UNIT)}
            </div>
          ))}
        </div>
      )}

      {(measureMode || measurements.length > 0) && (
        <div className="pointer-events-auto absolute top-16 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-panel-border bg-panel/85 px-3 py-1.5 text-xs backdrop-blur">
          <Ruler className="h-3.5 w-3.5 text-amber-300" />
          {measureMode ? (
            <span className="text-foreground">
              Click two points to measure
              <span className="text-muted"> · M or Esc to finish</span>
            </span>
          ) : (
            <span className="text-muted">
              {measurements.length} measurement{measurements.length === 1 ? '' : 's'}
            </span>
          )}
          {measurements.length > 0 && (
            <button
              type="button"
              onClick={clearMeasurements}
              className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-muted transition-colors hover:bg-white/8 hover:text-foreground"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>
      )}
    </>
  );
}
